import { useEffect, useRef, useState } from "react";
import { photosApi, API } from "@/lib/api";
import { Camera, Plus, Trash, X, CheckCircle, ArrowsClockwise, Crosshair } from "@phosphor-icons/react";
import { toast } from "sonner";
import { enqueue } from "@/lib/offlineQueue";

const KIND_LABELS = { pickup: "Pickup", dropoff: "Dropoff", damage: "Damage", other: "Other" };
const KIND_COLORS = { pickup: "#22c55e", dropoff: "#ff8c00", damage: "#ef4444", other: "#4682b4" };

export default function PhotoGallery({ jobId, allowUpload = true, compact = false, kinds = ["pickup", "dropoff", "damage", "other"] }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [uploadKind, setUploadKind] = useState(kinds[0]);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const data = await photosApi.list(jobId);
      setPhotos(data);
    } catch (e) {
      // noop
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [jobId]); // eslint-disable-line

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      if (!navigator.onLine) {
        await enqueue({
          kind: "multipart",
          method: "POST",
          url: `${API}/jobs/${jobId}/photos`,
          fileBlob: file,
          fileName: file.name || "photo.jpg",
          form: { kind: uploadKind },
        });
        toast.success("Photo queued (offline) — will upload when back online");
      } else {
        await photosApi.upload(jobId, file, uploadKind);
        toast.success("Photo uploaded");
        load();
      }
    } catch (err) {
      // network died mid-upload — fall back to queue
      try {
        await enqueue({
          kind: "multipart",
          method: "POST",
          url: `${API}/jobs/${jobId}/photos`,
          fileBlob: file,
          fileName: file.name || "photo.jpg",
          form: { kind: uploadKind },
        });
        toast.success("Photo queued for sync");
      } catch (_q) {
        toast.error(err.response?.data?.detail || "Upload failed");
      }
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onDelete = async (p) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await photosApi.remove(p.id);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  if (loading) {
    return <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717a] py-2">Loading photos…</div>;
  }

  return (
    <div data-testid="photo-gallery">
      {allowUpload && (
        <div className="flex items-center gap-2 mb-3">
          <div className="flex border border-[#2a2a2a]">
            {kinds.map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setUploadKind(k)}
                className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold transition-colors ${
                  uploadKind === k ? "bg-[#ff8c00] text-black" : "text-[#a1a1aa] hover:bg-[#1a1a1a]"
                }`}
                data-testid={`kind-${k}`}
              >
                {KIND_LABELS[k]}
              </button>
            ))}
          </div>
          <label className="ml-auto flex items-center gap-2 px-3 py-1.5 bg-[#4682b4] hover:bg-[#3a6d97] text-white text-[10px] uppercase tracking-[0.2em] font-bold cursor-pointer transition-colors" data-testid="upload-photo-btn">
            {uploading ? <ArrowsClockwise size={12} className="animate-spin" /> : <Camera size={12} weight="bold" />}
            {uploading ? "Uploading" : "Capture"}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={onFile}
              className="hidden"
              data-testid="photo-input"
              disabled={uploading}
            />
          </label>
        </div>
      )}

      {photos.length === 0 ? (
        <div className="border border-dashed border-[#2a2a2a] p-6 text-center text-[#71717a] text-[10px] uppercase tracking-[0.2em]">
          {allowUpload ? "No photos yet — Capture a pickup or dropoff shot" : "No photos for this job"}
        </div>
      ) : (
        <div className={`grid ${compact ? "grid-cols-3 sm:grid-cols-4" : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4"} gap-2`}>
          {photos.map((p) => (
            <div key={p.id} className="relative group border border-[#2a2a2a] bg-[#0a0a0a] aspect-square overflow-hidden" data-testid={`photo-${p.id}`}>
              <img
                src={photosApi.url(p.id)}
                alt={p.kind}
                className="w-full h-full object-cover cursor-pointer"
                onClick={() => setViewing(p)}
                loading="lazy"
              />
              <span
                className="absolute top-1 left-1 px-1.5 py-0.5 text-[9px] uppercase tracking-[0.2em] font-bold"
                style={{ background: KIND_COLORS[p.kind] || "#4682b4", color: "#000" }}
              >
                {KIND_LABELS[p.kind] || p.kind}
              </span>
              {p.watermarked && (
                <span
                  className="absolute bottom-1 left-1 px-1.5 py-0.5 text-[9px] uppercase tracking-[0.2em] font-bold bg-[#22c55e] text-black flex items-center gap-1"
                  title={`GPS-stamped${p.gps_lat != null ? ` · ${p.gps_lat.toFixed(5)}, ${p.gps_lng.toFixed(5)}` : ""}`}
                  data-testid={`photo-gps-badge-${p.id}`}
                >
                  <Crosshair size={8} weight="bold" /> GPS
                </span>
              )}
              {allowUpload && (
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete(p); }}
                  className="absolute top-1 right-1 w-6 h-6 bg-black/70 hover:bg-[#ef4444] text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                  data-testid={`delete-photo-${p.id}`}
                >
                  <Trash size={10} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {viewing && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-[3000] flex items-center justify-center p-4" onClick={() => setViewing(null)} data-testid="photo-viewer">
          <button onClick={() => setViewing(null)} className="absolute top-4 right-4 w-10 h-10 bg-[#1a1a1a] border border-[#4d4d4d] hover:border-[#ff8c00] flex items-center justify-center text-white">
            <X size={18} />
          </button>
          <img src={photosApi.url(viewing.id)} alt={viewing.kind} className="max-h-[90vh] max-w-[90vw] object-contain border border-[#4d4d4d]" />
          <div className="absolute bottom-4 left-4 right-4 max-w-md mx-auto bg-black/70 backdrop-blur-xl border border-white/10 px-4 py-2 text-xs font-mono text-white">
            <span style={{ color: KIND_COLORS[viewing.kind] }}>{KIND_LABELS[viewing.kind]}</span>
            <span className="mx-2 text-[#71717a]">·</span>
            <span className="text-[#a1a1aa]">{new Date(viewing.created_at).toLocaleString()}</span>
            {viewing.note && (<><span className="mx-2 text-[#71717a]">·</span><span>{viewing.note}</span></>)}
            {viewing.gps_lat != null && viewing.gps_lng != null && (
              <a
                href={`https://maps.google.com/?q=${viewing.gps_lat},${viewing.gps_lng}`}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-[10px] text-[#22c55e] hover:underline mt-1 flex items-center gap-1 w-fit"
                data-testid="photo-viewer-gps"
                title="Open POD capture coords on Google Maps"
              >
                <Crosshair size={9} weight="bold" />
                GPS {viewing.gps_lat.toFixed(5)}, {viewing.gps_lng.toFixed(5)}
                {viewing.gps_accuracy_m != null ? ` · ±${Math.round(viewing.gps_accuracy_m)} m` : ""}
                {viewing.watermarked ? " · watermarked" : ""}
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
